<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div class="d-flex align-items-center">
          <router-link :to="{ name: 'siteManagement' }" class="btn btn-sm btn-outline-secondary mr-3">
            <i class="fas fa-arrow-left mr-1"></i> Back
          </router-link>
          <div>
            <p class="text-uppercase text-muted mb-0 super-page-kicker">Site Detail</p>
            <h2 class="h3 mb-0">{{ siteInfo.name || 'Site Detail' }}</h2>
          </div>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <!-- Site summary card -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-center">
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0">
              <p class="super-page-kicker mb-1">Site Name</p>
              <h4 class="mb-0">{{ siteInfo.name || '—' }}</h4>
            </div>
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0" v-if="serviceInfo">
              <p class="super-page-kicker mb-1">Meter Type</p>
              <h4 class="mb-0">{{ serviceInfo.service_name }}</h4>
            </div>
            <div class="col-sm-4 col-lg-3 mb-3 mb-sm-0">
              <p class="super-page-kicker mb-1">Total Lots</p>
              <h4 class="mb-0">{{ siteDetails.data ? siteDetails.data.total : '—' }}</h4>
            </div>
            <div class="col-lg-3 d-flex justify-content-lg-end mt-3 mt-lg-0">
              <b-button variant="primary" class="mr-2" @click="importFile">
                <i class="fas fa-upload mr-1"></i> Import Lots
              </b-button>
              <a :href="exportUrl" target="_blank" class="btn btn-outline-primary">
                <i class="fas fa-download mr-1"></i> Export Lots
              </a>
            </div>
          </div>
        </div>
      </div>

      <b-tabs pills class="mb-5">

        <!-- View Lots tab -->
        <b-tab title="Lots" active>

          <!-- Import new lots -->
          <div class="card shadow-sm border-0 mb-4 mt-4">
            <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div>
                <h3 class="mb-0">Import Lots</h3>
                <p class="text-muted small mb-0 mt-1">
                  Add new lots from CSV. Include <strong>Quote Part (%)</strong> when available.
                </p>
              </div>
              <b-button variant="outline-primary" size="sm" @click="importFlag = !importFlag">
                <i :class="importFlag ? 'fas fa-times' : 'fas fa-upload'" class="mr-1"></i>
                {{ importFlag ? 'Cancel' : 'Upload CSV' }}
              </b-button>
            </div>
            <div v-if="importFlag" class="card-body pt-0">
              <div class="row align-items-end">
                <div class="col-lg-7 mb-3 mb-lg-0">
                  <b-form-file accept=".csv" v-model="uploadFile" placeholder="Choose a CSV file..."></b-form-file>
                </div>
                <div class="col-lg-5 d-flex align-items-center">
                  <b-button variant="success" :disabled="!uploadFile || loading" @click="fileImport(uploadFile)" class="mr-3">
                    <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-upload mr-1"></i>
                    {{ loading ? 'Uploading…' : 'Upload' }}
                  </b-button>
                  <b-link :href="siteCsvDump + 'site-csv-dump'" target="_blank" class="text-muted small">
                    <i class="fas fa-file-csv mr-1"></i> Sample file
                  </b-link>
                </div>
              </div>
              <div v-if="importErrors.length" class="mt-3">
                <div class="alert alert-danger mb-0">
                  <p class="font-weight-bold mb-2">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    {{ importErrors.length }} validation error{{ importErrors.length > 1 ? 's' : '' }} — no lots were imported
                  </p>
                  <ul class="mb-0 pl-3">
                    <li v-for="(err, i) in importErrors" :key="i" class="small">{{ err }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Batch update existing lots -->
          <div class="card shadow-sm border-0 mb-4">
            <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div>
                <h3 class="mb-0">Batch Update</h3>
                <p class="text-muted small mb-0 mt-1">Download a pre-filled template, edit it, then re-upload to update lot data in bulk.</p>
              </div>
              <b-button variant="outline-info" size="sm" @click="batchFlag = !batchFlag">
                <i :class="batchFlag ? 'fas fa-times' : 'fas fa-file-csv'" class="mr-1"></i>
                {{ batchFlag ? 'Cancel' : 'Batch Update' }}
              </b-button>
            </div>
            <div v-if="batchFlag" class="card-body pt-0">
              <div class="mb-4">
                <p class="font-weight-bold mb-2 small">Step 1 — Download template</p>
                <b-button variant="outline-info" :disabled="batchDownloading" @click="downloadBatchTemplate">
                  <b-spinner v-if="batchDownloading" small class="mr-1"></b-spinner>
                  <i v-else class="fas fa-download mr-2"></i>
                  {{ batchDownloading ? 'Downloading…' : 'Download CSV Template' }}
                </b-button>
              </div>
              <div>
                <p class="font-weight-bold mb-2 small">Step 2 — Upload &amp; apply</p>
                <div class="d-flex align-items-end flex-wrap" style="gap: 0.75rem;">
                  <b-form-file v-model="batchFile" accept=".csv" placeholder="Choose edited CSV…"
                    browse-text="Browse" style="max-width: 340px;"></b-form-file>
                  <b-button variant="success" :disabled="!batchFile || batchUploading" @click="submitBatchUpload">
                    <b-spinner v-if="batchUploading" small class="mr-1"></b-spinner>
                    {{ batchUploading ? 'Uploading…' : 'Upload & Apply' }}
                  </b-button>
                </div>
                <div v-if="batchResult" class="mt-3">
                  <b-alert :variant="batchResult.failed > 0 ? 'warning' : 'success'" show class="mb-0">
                    <strong>{{ batchResult.message }}</strong>
                    <p v-if="batchResult.failed > 0" class="mb-0 mt-1 small">
                      Check that barcodes belong to this site and service type, and dates are in <code>DD/MM/YYYY</code> format.
                    </p>
                  </b-alert>
                </div>
              </div>
            </div>
          </div>

          <!-- Lots table -->
          <div class="card shadow-sm border-0 mt-4">
            <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <h3 class="mb-0">Lots</h3>
              <span v-if="lotsList.total" class="badge badge-pill badge-primary">{{ lotsList.total }} total</span>
            </div>

            <div v-if="loading" class="text-center py-6">
              <b-spinner variant="primary" class="mb-3"></b-spinner>
              <p class="text-muted mb-0">Loading lots...</p>
            </div>

            <div v-else class="table-responsive">
              <b-table class="mb-0" striped hover :items="lotsList.data" :fields="fields">
                <template slot="index" slot-scope="data">
                  {{ data.index + lotsList.from }}
                </template>
                <template slot="lot_no" slot-scope="data">
                  <div class="d-flex align-items-center justify-content-center" style="gap: 0.4rem;">
                    <span>{{ data.value }}</span>
                    <b-button variant="link" size="sm" class="p-0 text-muted" @click="openRenameModal(data.item)"
                      v-b-tooltip.hover title="Rename lot">
                      <i class="fas fa-pencil-alt" style="font-size: 0.7rem;"></i>
                    </b-button>
                  </div>
                </template>
                <template slot="ownerName" slot-scope="data">
                  {{ data.item.lot_owner ? data.item.lot_owner.name : '—' }}
                </template>
                <template slot="barcode" slot-scope="data">
                  {{ data.item.barcode ? data.item.barcode.barcode : '—' }}
                </template>
              </b-table>
        </div>

              <div class="card-footer border-0 bg-white" v-if="lotsList.total > 10">
                <b-pagination align="right" @input="pageClick(currentPage)" :total-rows="lotsList.total"
                  v-model="currentPage" :per-page="10">
                </b-pagination>
              </div>
            </div>

        </b-tab>

        <!-- Edit tab -->
        <b-tab title="Edit">

          <div class="card shadow-sm border-0 mt-4">
            <div class="card-body py-4">
              <div class="d-flex align-items-start">
                <i class="fas fa-info-circle text-primary mr-3 mt-1"></i>
                <div>
                  <p class="mb-2">Site configuration — including tax label and rate — is edited from the Site Management page.</p>
                  <router-link :to="{ name: 'siteManagement' }" class="btn btn-sm btn-outline-primary">
                    Go to Site Management
                  </router-link>
                </div>
              </div>
            </div>
          </div>

        </b-tab>

      </b-tabs>

    </div>

    <!-- Rename lot modal -->
    <b-modal ref="renameModal" title="Rename Lot Number" centered hide-footer
      no-close-on-backdrop no-close-on-esc size="sm">
      <div class="mb-3">
        <p class="text-muted small mb-1">Current lot number</p>
        <p class="font-weight-bold mb-0">{{ rename.lotNo }}</p>
      </div>
      <b-form-group label="New lot number" class="mb-4">
        <b-form-input
          v-model.trim="rename.newLotNo"
          :state="rename.error ? false : null"
          placeholder="e.g. A102"
          autofocus
          @keydown.enter.native.prevent="submitRename"
        ></b-form-input>
        <b-form-invalid-feedback>{{ rename.error }}</b-form-invalid-feedback>
      </b-form-group>
      <div class="d-flex justify-content-end" style="gap: 0.5rem;">
        <b-button variant="outline-secondary" @click="$refs.renameModal.hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="!rename.newLotNo || rename.loading" @click="submitRename">
          <b-spinner v-if="rename.loading" small class="mr-1"></b-spinner>
          {{ rename.loading ? 'Renaming…' : 'Rename' }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';
import ApiServices from '@/services/api';
import { apiUrl } from '@/config'
export default {
  components: {
    userData
  },
  data() {
    return {
      currentPage: 1,
      totalPageCount: 1,
      loading: false,
      siteCsvDump: apiUrl,
      exportUrl: '',
      service_provider_id: this.$route.params.serviceproviderid,
      site_id: this.$route.params.siteid,
      service_type_id: this.$route.params.servicetypeid,
      importFlag: false,
      importErrors: [],
      importData: "",
      siteInfo: '',
      serviceInfo: '',
      uploadFile: '',
      lotsList: [],
      batchFlag: false,
      batchFile: null,
      batchDownloading: false,
      batchUploading: false,
      batchResult: null,
      rename: { lotNo: '', newLotNo: '', loading: false, error: '' },
      fields: [
        { key: 'index',         label: 'S.No',          sortable: false, class: 'text-center' },
        { key: 'lot_no',        label: 'Lot No',        sortable: true,  class: 'text-center' },
        { key: 'ownerName',     label: 'Owner Name',    sortable: true,  class: 'text-center' },
        { key: 'barcode',       label: 'Barcode',       sortable: false, class: 'text-center' },
        { key: 'previous_unit', label: 'Previous Unit', sortable: true,  class: 'text-center' }
      ],
    }
  },
  computed: {
    ...mapGetters('siteManagement', ['siteDetails', 'sampleFile']),
    normalizedServiceTypeId() {
      return this.service_type_id || 1;
    },
  },
  methods: {
    ...mapActions(['FILE_UPLOAD']),
    ...mapActions('siteManagement', ['SITE_DETAILS']),
    getSiteSiteDetail() {
      this.loading = true
      const data = {
        service_provider_id: this.service_provider_id,
        service_type_id: this.normalizedServiceTypeId,
        site_id: this.site_id,
        page: this.currentPage
      }
      this.exportUrl = `${apiUrl}site-csv?service_provider_id=${this.service_provider_id}&site_id=${this.site_id}&service_type_id=${this.normalizedServiceTypeId}`
      this.SITE_DETAILS(data).then(() => {
        this.siteInfo = this.siteDetails.site_name
        this.serviceInfo = this.siteDetails.service
        this.lotsList = this.siteDetails.data
      }).catch(() => {
      }).finally(() => {
        this.loading = false
      })
    },
    pageClick(data) {
      this.currentPage = data
      this.getSiteSiteDetail();
    },
    importFile() {
      this.importFlag = !this.importFlag
      this.uploadFile = ''
      this.importErrors = []
    },
    fileImport(data) {
      this.loading = true;
      this.importErrors = []
      const formData = new FormData()
      formData.append('site_id', this.site_id)
      formData.append('service_type_id', this.normalizedServiceTypeId)
      formData.append('service_provider_id', this.service_provider_id);
      formData.append('file', data);
      this.FILE_UPLOAD(formData).then((response) => {
        if (response && response.status) {
          this.SUCCESS_TOAST_MSG('File uploaded successfully')
          this.importFlag = false
          this.importErrors = []
          this.uploadFile = ''
          this.getSiteSiteDetail()
        } else if (response && response.errors && response.errors.length) {
          this.importErrors = response.errors
        } else if (response && response.message) {
          this.ERROR_TOAST_MSG(response.message)
        }
      }).catch(() => { }).finally(() => {
        this.loading = false
      })
    },
    openRenameModal(item) {
      this.rename = { lotNo: item.lot_no, newLotNo: '', loading: false, error: '' }
      this.$refs.renameModal.show()
    },

    submitRename() {
      if (!this.rename.newLotNo) return
      if (this.rename.newLotNo === this.rename.lotNo) {
        this.rename.error = 'New lot number must differ from the current one.'
        return
      }
      const confirmed = window.confirm(
        `This will rename lot ${this.rename.lotNo} to ${this.rename.newLotNo} across all utility types on this site. Continue?`
      )
      if (!confirmed) return

      this.rename.loading = true
      this.rename.error = ''
      const token = localStorage.getItem('token')
      fetch(`${apiUrl}lots/rename`, {
        method: 'PATCH',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_provider_id: Number(this.service_provider_id),
          site_id: Number(this.site_id),
          lot_no: this.rename.lotNo,
          new_lot_no: this.rename.newLotNo
        })
      })
        .then(res => res.json().then(data => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (ok && data.status) {
            const extra = data.data && data.data.lots_updated > 1
              ? ` (${data.data.lots_updated} service types updated)`
              : ''
            this.SUCCESS_TOAST_MSG(`${data.message}${extra}`)
            this.$refs.renameModal.hide()
            this.getSiteSiteDetail()
          } else {
            this.rename.error = data.message || 'Rename failed.'
          }
        })
        .catch(() => {
          this.rename.error = 'Network error. Please try again.'
        })
        .finally(() => {
          this.rename.loading = false
        })
    },

    async downloadBatchTemplate() {
      this.batchDownloading = true
      try {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams({
          site_id: this.site_id,
          service_type_id: this.normalizedServiceTypeId
        })
        const res = await fetch(`${apiUrl}lots-batch-update-template?${params}`, {
          headers: { Authorization: token }
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const disposition = res.headers.get('Content-Disposition') || ''
        const match = disposition.match(/filename=(.+)/)
        const filename = match ? match[1].trim() : 'batch_template.csv'
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        this.ERROR_TOAST_MSG('Failed to download template. Please try again.')
      } finally {
        this.batchDownloading = false
      }
    },

    submitBatchUpload() {
      this.batchUploading = true
      this.batchResult = null
      const formData = new FormData()
      formData.append('file', this.batchFile)
      formData.append('service_provider_id', this.service_provider_id)
      formData.append('site_id', this.site_id)
      formData.append('service_type_id', this.normalizedServiceTypeId)
      ApiServices.uploadBatchUpdate(formData)
        .then(response => {
          const message = (response && response.message) || 'Batch update complete.'
          const failedMatch = message.match(/Failed:\s*(\d+)/i)
          const failed = failedMatch ? parseInt(failedMatch[1]) : 0
          this.batchResult = { message, failed }
          if (failed === 0) {
            this.SUCCESS_TOAST_MSG(message)
            this.batchFile = null
            this.getSiteSiteDetail()
          }
        })
        .catch(err => {
          this.ERROR_TOAST_MSG((err && err.message) || 'Upload failed. Check your file and try again.')
        })
        .finally(() => {
          this.batchUploading = false
        })
    },

  },
  created() {
    this.getSiteSiteDetail(1);
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5e72e4;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/siteDetailView.vue